/* ---------------------------------------------------------------------------
 * Couples Sync — PDP "Add to Couples Folder" button (v12.17.1)
 *
 * Wires up every [data-cs-pdp-save] button on PDPs to the deployed Couples
 * Sync Worker at https://couples.mydiamonds.com.au. Behaviour:
 *
 *   1. Click button → check localStorage.csActiveFolderId.
 *      - If folder ID exists → POST /api/folder/:id/item directly (no modal).
 *      - Else → open the cs-pdp-modal.
 *   2. Modal submit (default state) → POST /api/folder/invite with partnerA,
 *      partnerB?, initialItem. On success cache folder ID + transform button.
 *   3. Modal "Have a folder already? Paste the ID" → POST /api/folder/:id/item
 *      with the pasted ID; on success cache + transform.
 *   4. Saved state: button label becomes "Saved to your folder", with a small
 *      "View folder →" link opening the folder URL in a new tab.
 *
 * Endpoints called (verified against couples-sync/src/index.ts on 2026-06-01):
 *   POST  https://couples.mydiamonds.com.au/api/folder/invite
 *     body: {partnerA:{name,email}, partnerB?:{email}, initialItem?:{...}}
 *     → 200 {id, url, inviteSent} + Set-Cookie
 *   POST  https://couples.mydiamonds.com.au/api/folder/:id/item
 *     cookie: partner identity; body: {productHandle, title, metaLine,
 *     thumbnailUrl, tag:'a', addedBy:'a'}
 *     → 200 {item, folder}
 *
 * All fetches use credentials:'include' because the worker sets
 * Access-Control-Allow-Credentials:true and we need the partner cookie to
 * round-trip on cross-origin requests.
 *
 * Privacy-hardened browsers (Brave, Safari, latest Chrome) need
 * https://www.mydiamonds.com.au + https://mydiamonds.com.au in the worker
 * ALLOWED_ORIGINS — both are present (verified, lines 110–118 of index.ts).
 * ------------------------------------------------------------------------- */

(function () {
  'use strict';

  var WORKER_BASE = 'https://couples.mydiamonds.com.au';
  var LS_FOLDER_ID = 'csActiveFolderId';
  var LS_PARTNER_NAME = 'csPartnerAName';
  var LS_PARTNER_EMAIL = 'csPartnerAEmail';
  var FOLDER_URL_BASE = WORKER_BASE + '/c/';

  // ── localStorage with safe fallback (private-browsing throws) ────────────
  var storage = (function () {
    var mem = {};
    var ok = false;
    try {
      var key = '__cs_probe__' + Date.now();
      window.localStorage.setItem(key, '1');
      window.localStorage.removeItem(key);
      ok = true;
    } catch (_e) { ok = false; }
    return {
      available: ok,
      get: function (k) { try { return ok ? window.localStorage.getItem(k) : (mem[k] || null); } catch (_e) { return mem[k] || null; } },
      set: function (k, v) { try { if (ok) window.localStorage.setItem(k, v); else mem[k] = v; } catch (_e) { mem[k] = v; } },
      remove: function (k) { try { if (ok) window.localStorage.removeItem(k); else delete mem[k]; } catch (_e) { delete mem[k]; } }
    };
  })();

  // ── DOM helpers ──────────────────────────────────────────────────────────
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function isValidEmail(s) { return !!s && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s); }
  function isValidHandle(s) { return !!s && /^[a-z0-9-]+$/.test(s); }
  function isValidFolderId(s) { return !!s && /^[A-Z2-9]{6,10}$/.test(s); }

  // ── Toast ────────────────────────────────────────────────────────────────
  var toastEl = null;
  var toastTimer = null;
  function showToast(message, ms) {
    toastEl = toastEl || document.getElementById('cs-pdp-toast');
    if (!toastEl) return;
    toastEl.textContent = message;
    toastEl.setAttribute('data-shown', 'true');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.setAttribute('data-shown', 'false');
    }, ms || 4200);
  }

  // ── Button state transformation ──────────────────────────────────────────
  function transformButtonToSaved(btn, folderId) {
    if (!btn) return;
    btn.setAttribute('data-cs-saved', 'true');
    btn.setAttribute('aria-pressed', 'true');
    var label = btn.querySelector('[data-cs-pdp-label]');
    if (label) label.textContent = 'Saved to your folder';
    // Inject view-folder link as a sibling, once per button.
    if (!btn.parentNode.querySelector('[data-cs-view-link][data-cs-for="' + btn.dataset.csProductHandle + '"]')) {
      var a = document.createElement('a');
      a.className = 'cs-pdp-view-link';
      a.setAttribute('data-cs-view-link', '');
      a.setAttribute('data-cs-for', btn.dataset.csProductHandle || '');
      a.href = FOLDER_URL_BASE + encodeURIComponent(folderId);
      a.target = '_blank';
      a.rel = 'noopener';
      a.textContent = 'View folder →';
      btn.parentNode.insertBefore(a, btn.nextSibling);
    }
  }
  function resetButton(btn, originalLabel) {
    if (!btn) return;
    btn.removeAttribute('data-cs-saved');
    btn.setAttribute('aria-pressed', 'false');
    var label = btn.querySelector('[data-cs-pdp-label]');
    if (label) label.textContent = originalLabel || 'Add to Couples Folder';
  }
  function showInlineError(btn, message) {
    if (!btn || !btn.parentNode) return;
    var prev = btn.parentNode.querySelector('[data-cs-inline-error]');
    if (prev) prev.parentNode.removeChild(prev);
    var span = document.createElement('span');
    span.className = 'cs-pdp-inline-error';
    span.setAttribute('data-cs-inline-error', '');
    span.textContent = message;
    btn.parentNode.insertBefore(span, btn.nextSibling);
    setTimeout(function () { if (span.parentNode) span.parentNode.removeChild(span); }, 5500);
  }

  // ── Build the initialItem payload from a PDP button ──────────────────────
  function buildItemFromButton(btn) {
    var handle = (btn.getAttribute('data-cs-product-handle') || '').trim();
    var title = (btn.getAttribute('data-cs-title') || '').trim();
    if (!isValidHandle(handle) || !title) return null;
    var item = {
      productHandle: handle,
      title: title,
      tag: 'a',
      addedBy: 'a'
    };
    var meta = (btn.getAttribute('data-cs-meta-line') || '').trim();
    if (meta) item.metaLine = meta;
    var thumb = (btn.getAttribute('data-cs-thumbnail-url') || '').trim();
    if (thumb) item.thumbnailUrl = thumb;
    var varId = parseInt(btn.getAttribute('data-cs-variant-id') || '', 10);
    if (!isNaN(varId) && varId > 0) item.variantId = varId;
    return item;
  }

  // ── API calls ────────────────────────────────────────────────────────────
  function apiInvite(partnerA, partnerB, initialItem) {
    var body = { partnerA: partnerA };
    if (partnerB && partnerB.email) body.partnerB = partnerB;
    if (initialItem) body.initialItem = initialItem;
    return fetch(WORKER_BASE + '/api/folder/invite', {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (j) {
        if (!res.ok) throw new Error((j && j.error) || ('Save failed (' + res.status + ')'));
        return j;
      });
    });
  }

  function apiAddItem(folderId, item) {
    return fetch(WORKER_BASE + '/api/folder/' + encodeURIComponent(folderId) + '/item', {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item)
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (j) {
        if (res.status === 401 || res.status === 403) {
          var err = new Error('cookie-expired');
          err.code = 'NO_COOKIE';
          throw err;
        }
        if (!res.ok) throw new Error((j && j.error) || ('Save failed (' + res.status + ')'));
        return j;
      });
    });
  }

  // ── Modal control ────────────────────────────────────────────────────────
  var activeBtn = null;
  var activeItem = null;
  var lastFocusBeforeModal = null;

  function openModal(btn, item) {
    var modal = document.getElementById('cs-pdp-modal');
    if (!modal) return;
    activeBtn = btn;
    activeItem = item;
    lastFocusBeforeModal = document.activeElement;
    // Prefill from prior session.
    var nameInput = document.getElementById('cs-pdp-name');
    var emailInput = document.getElementById('cs-pdp-email');
    if (nameInput && !nameInput.value) nameInput.value = storage.get(LS_PARTNER_NAME) || '';
    if (emailInput && !emailInput.value) emailInput.value = storage.get(LS_PARTNER_EMAIL) || '';
    hideError();
    closePaste();
    modal.setAttribute('data-open', 'true');
    modal.setAttribute('aria-hidden', 'false');
    setTimeout(function () { (nameInput || $('input', modal)).focus(); }, 30);
    document.addEventListener('keydown', escListener);
  }
  function closeModal() {
    var modal = document.getElementById('cs-pdp-modal');
    if (!modal) return;
    modal.setAttribute('data-open', 'false');
    modal.setAttribute('aria-hidden', 'true');
    document.removeEventListener('keydown', escListener);
    if (lastFocusBeforeModal && lastFocusBeforeModal.focus) {
      try { lastFocusBeforeModal.focus(); } catch (_e) {}
    }
    activeBtn = null;
    activeItem = null;
  }
  function escListener(ev) {
    if (ev.key === 'Escape' || ev.keyCode === 27) closeModal();
  }
  function showError(msg) {
    var el = document.getElementById('cs-pdp-error');
    if (!el) return;
    el.textContent = msg;
    el.setAttribute('data-shown', 'true');
  }
  function hideError() {
    var el = document.getElementById('cs-pdp-error');
    if (!el) return;
    el.setAttribute('data-shown', 'false');
  }
  function openPaste() {
    var p = document.getElementById('cs-pdp-paste');
    if (p) p.setAttribute('data-open', 'true');
    var s = document.getElementById('cs-pdp-submit');
    if (s) s.textContent = 'Add to my folder';
  }
  function closePaste() {
    var p = document.getElementById('cs-pdp-paste');
    if (p) p.setAttribute('data-open', 'false');
    var s = document.getElementById('cs-pdp-submit');
    if (s) s.textContent = 'Save the ring + start the folder';
    var f = document.getElementById('cs-pdp-folder-id');
    if (f) f.value = '';
  }

  // ── Click delegation: any [data-cs-pdp-save] button ──────────────────────
  document.addEventListener('click', function (ev) {
    var btn = ev.target && (ev.target.closest ? ev.target.closest('[data-cs-pdp-save]') : null);
    if (!btn) return;
    if (btn.getAttribute('data-cs-saved') === 'true') {
      // Already saved on this PDP — let the view-folder link handle it.
      ev.preventDefault();
      return;
    }
    ev.preventDefault();
    var item = buildItemFromButton(btn);
    if (!item) {
      showInlineError(btn, 'Could not read product details, please refresh.');
      return;
    }
    var folderId = storage.available ? storage.get(LS_FOLDER_ID) : null;
    if (folderId && isValidFolderId(folderId)) {
      handleDirectAdd(btn, folderId, item);
    } else {
      openModal(btn, item);
    }
  });

  // ── Direct add path (folder cached, no modal) ────────────────────────────
  function handleDirectAdd(btn, folderId, item) {
    var label = btn.querySelector('[data-cs-pdp-label]');
    var originalLabel = label ? label.textContent : 'Add to Couples Folder';
    if (label) label.textContent = 'Saving…';
    btn.disabled = true;
    apiAddItem(folderId, item).then(function (_resp) {
      btn.disabled = false;
      transformButtonToSaved(btn, folderId);
      showToast('Added to your folder.');
    }).catch(function (err) {
      btn.disabled = false;
      if (err && err.code === 'NO_COOKIE') {
        // Identity expired — clear cache and prompt re-create / re-paste.
        storage.remove(LS_FOLDER_ID);
        if (label) label.textContent = originalLabel;
        openModal(btn, item);
        return;
      }
      if (label) label.textContent = originalLabel;
      showInlineError(btn, (err && err.message) || 'Could not save, please try again.');
    });
  }

  // ── Modal wiring ─────────────────────────────────────────────────────────
  document.addEventListener('click', function (ev) {
    var t = ev.target;
    if (!t) return;
    if (t.closest('[data-cs-close]')) { ev.preventDefault(); closeModal(); return; }
    if (t.id === 'cs-pdp-modal') { closeModal(); return; }
    if (t.id === 'cs-pdp-expand') {
      ev.preventDefault();
      var p = document.getElementById('cs-pdp-paste');
      if (p && p.getAttribute('data-open') === 'true') closePaste();
      else openPaste();
    }
  });

  document.addEventListener('submit', function (ev) {
    var form = ev.target;
    if (!form || form.id !== 'cs-pdp-form') return;
    ev.preventDefault();
    if (!activeBtn || !activeItem) return;

    var nameVal = ($('#cs-pdp-name') || {}).value || '';
    var emailVal = ($('#cs-pdp-email') || {}).value || '';
    var partnerVal = ($('#cs-pdp-partner-email') || {}).value || '';
    var pasteWrap = document.getElementById('cs-pdp-paste');
    var pasteOpen = pasteWrap && pasteWrap.getAttribute('data-open') === 'true';
    var pasteVal = (($('#cs-pdp-folder-id') || {}).value || '').trim().toUpperCase();

    hideError();

    if (pasteOpen) {
      if (!isValidFolderId(pasteVal)) {
        showError('Please paste a valid folder ID (letters and numbers, 6–10 chars).');
        return;
      }
      runSubmit(function () { return apiAddItem(pasteVal, activeItem); }, pasteVal, /*isCreate*/false, partnerVal);
      return;
    }

    nameVal = nameVal.trim().slice(0, 120);
    emailVal = emailVal.trim().slice(0, 240);
    partnerVal = partnerVal.trim().slice(0, 240);

    if (!nameVal) { showError('Please add your name.'); return; }
    if (!isValidEmail(emailVal)) { showError('Please add a valid email.'); return; }
    if (partnerVal && !isValidEmail(partnerVal)) { showError('Partner email looks off, please check.'); return; }
    if (partnerVal && partnerVal.toLowerCase() === emailVal.toLowerCase()) {
      showError('Partner email needs to be different from yours.');
      return;
    }

    storage.set(LS_PARTNER_NAME, nameVal);
    storage.set(LS_PARTNER_EMAIL, emailVal);

    runSubmit(
      function () {
        return apiInvite(
          { name: nameVal, email: emailVal },
          partnerVal ? { email: partnerVal } : null,
          activeItem
        );
      },
      null,
      /*isCreate*/true,
      partnerVal
    );
  });

  function runSubmit(apiCall, presetFolderId, isCreate, partnerEmail) {
    var submitBtn = document.getElementById('cs-pdp-submit');
    var prevLabel = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Saving…'; }
    apiCall().then(function (resp) {
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = prevLabel; }
      var folderId = presetFolderId;
      var inviteSent = false;
      if (isCreate && resp && resp.id) {
        folderId = resp.id;
        inviteSent = !!resp.inviteSent;
      }
      if (!folderId) { showError('Server did not return a folder ID. Please try again.'); return; }
      storage.set(LS_FOLDER_ID, folderId);
      if (activeBtn) transformButtonToSaved(activeBtn, folderId);
      closeModal();
      if (isCreate) {
        if (partnerEmail && inviteSent) {
          showToast('Saved to your folder. We’ve sent your partner the link.');
        } else if (partnerEmail && !inviteSent) {
          showToast('Folder created, share the link with ' + partnerEmail + '.');
        } else {
          showToast('Saved to your folder.');
        }
      } else {
        showToast('Added to your folder.');
      }
    }).catch(function (err) {
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = prevLabel; }
      var msg = (err && err.message) || 'Save failed, please try again.';
      if (err && err.code === 'NO_COOKIE') msg = 'That folder isn’t recognised on this device. Start a new one above, or paste a fresh ID.';
      showError(msg);
    });
  }

  // ── If the PDP page already matches a previously saved folder, show the
  //    "Saved" state on load. This is a lightweight UX cue — we don't probe
  //    the worker on every page load, just trust the cached ID. ────────────
  function applyInitialSavedState() {
    var folderId = storage.available ? storage.get(LS_FOLDER_ID) : null;
    if (!folderId || !isValidFolderId(folderId)) return;
    // We don't know without a fetch whether THIS product is in the folder.
    // Leaving the button in default state until the user clicks keeps the
    // UX honest: "Add" → success → "Saved". Avoids a misleading "Saved"
    // label on a product that isn't actually in the folder yet.
    void folderId;
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyInitialSavedState);
  } else {
    applyInitialSavedState();
  }
})();
